import { createContext, useContext, useEffect, useState, useRef, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";
import type { UserRole } from "@/lib/navigation";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  role: UserRole | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null, session: null, role: null, loading: true,
  signOut: async () => {},
});

const HOUR = 60 * 60 * 1000;
const SESSION_TIMEOUTS_MS: Record<UserRole, number> = {
  barman: 8 * HOUR,
  cuisinier: 8 * HOUR,
  serveur: 8 * HOUR,
  chef_salle: 8 * HOUR,
  caissiere: 8 * HOUR,
  gestionnaire: 8 * HOUR,
  controleur: 4 * HOUR,
  gerante: 4 * HOUR,
  admin: 4 * HOUR,
  pdg: 3 * HOUR,
};
const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = ["mousemove", "keydown", "click"];

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [role, setRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);
  const inactivityTimerRef = useRef<number | null>(null);

  useEffect(() => {
    let mounted = true;

    const fetchRole = async (userId: string): Promise<UserRole | null> => {
      try {
        const { data, error } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", userId)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error) throw error;
        return (data?.role as UserRole | null) ?? null;
      } catch (e) {
        console.error("fetchRole error:", e);
        return null;
      }
    };

    const syncAuthState = (s: Session | null) => {
      if (!mounted) return;

      setSession(s);
      setUser(s?.user ?? null);

      if (!s?.user) {
        setRole(null);
        setLoading(false);
        return;
      }

      // Le rôle est chargé en parallèle — on lève `loading` immédiatement
      // pour que la navigation ne soit pas bloquée par cette requête.
      setLoading(false);
      fetchRole(s.user.id).then((nextRole) => {
        if (mounted) setRole(nextRole);
      });
    };

    // 1) On pose d'abord le listener (sinon on rate les events précoces),
    //    puis on récupère la session en cours.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, s) => {
      syncAuthState(s);
      if (event === "SIGNED_IN" && s?.user) {
        // Track last login (fire-and-forget)
        supabase.from("profiles")
          .update({ derniere_connexion: new Date().toISOString() })
          .eq("user_id", s.user.id)
          .then(() => {});
      }
      // Session expirée côté serveur (refresh token invalide / révoqué)
      // → redirection unifiée vers /login?expired=1
      if (event === "SIGNED_OUT" && !s) {
        const path = window.location.pathname;
        const isPublic =
          path === "/login" ||
          path === "/forgot-password" ||
          path === "/reset-password" ||
          path === "/confidentialite" ||
          path === "/cgu";
        if (!isPublic) {
          window.location.replace("/login?expired=1");
        }
      }
    });

    supabase.auth.getSession().then(({ data: { session: s } }) => {
      syncAuthState(s);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Inactivity auto-logout: per-role timeout reset on user activity
  useEffect(() => {
    if (!user || !role) return;
    const timeoutMs = SESSION_TIMEOUTS_MS[role];
    if (!timeoutMs) return;

    const expireSession = async () => {
      try {
        await supabase.auth.signOut({ scope: "local" });
      } catch (e) {
        console.error("inactivity signOut error:", e);
      }
      window.location.replace("/login?expired=1");
    };

    const resetTimer = () => {
      if (inactivityTimerRef.current !== null) {
        window.clearTimeout(inactivityTimerRef.current);
      }
      inactivityTimerRef.current = window.setTimeout(expireSession, timeoutMs);
    };

    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, resetTimer, { passive: true }));
    resetTimer();

    return () => {
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, resetTimer));
      if (inactivityTimerRef.current !== null) {
        window.clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
    };
  }, [user, role]);

  const signOut = async () => {
    setUser(null);
    setSession(null);
    setRole(null);
    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch (e) {
      console.error("signOut error:", e);
    }
    window.location.replace("/login");
  };

  return (
    <AuthContext.Provider value={{ user, session, role, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
